import type {
  CellOrdersState,
  OrderDraft,
  OrderRejection,
  OrderWarningWire,
} from '@badger-sentinel/protocol';

/**
 * The orders queue (design 424–467), and the index problem underneath it.
 *
 * ## `index` is a position in the array the client sent
 *
 * `OrderRejection.index` and `OrderWarningWire.index` are offsets into the
 * `orders[]` of one `orders.draft` frame — not into anything the client is
 * currently holding. Rejections arrive asynchronously (shell.md §Orders), and
 * every queue edit sends a fresh whole set, so a reorder between the send and
 * the response points the message at the wrong row. The wire gives no
 * correlation id to fix it with.
 *
 * So the queue carries a **stable key per order**, generated on the way in and
 * kept across a reorder, an edit and a duplicate; each send records the key
 * order it went out with; and `alignFeedback` matches an arriving
 * `CellOrdersState` back to the send that produced it *by its content*.
 *
 * The match is exact and not a heuristic: the server stores the accepted subset
 * in sent order and reports the rejected ones by index, so
 * `sent.filter((_, i) => !rejected.has(i))` reconstructs `state.accepted`
 * element for element. A send that reconstructs it is the send that produced it.
 * Newest first, because two identical sends are indistinguishable and the newest
 * is the one the operator is looking at.
 *
 * When nothing matches — a page that reloaded, or a snapshot the client never
 * asked for — the rejections are returned **unplaced** rather than pinned to a
 * row by position, because a rejection on the wrong row is worse than one with
 * no row.
 */

export interface QueuedOrder {
  /** Stable for the life of this order. Never an index, never derived from one. */
  readonly key: string;
  readonly order: OrderDraft;
}

export interface SentDraft {
  readonly seq: number;
  readonly keys: readonly string[];
  readonly orders: readonly OrderDraft[];
}

export interface OrderFeedback {
  readonly rejections: ReadonlyMap<string, OrderRejection>;
  readonly warnings: ReadonlyMap<string, readonly OrderWarningWire[]>;
  /** Rejections that could not be matched to a send. Never guessed onto a row. */
  readonly unplaced: readonly OrderRejection[];
  readonly matchedSeq: number | null;
}

export const NO_FEEDBACK: OrderFeedback = Object.freeze({
  rejections: new Map<string, OrderRejection>(),
  warnings: new Map<string, readonly OrderWarningWire[]>(),
  unplaced: [],
  matchedSeq: null,
});

let keySeq = 0;

/** A fresh key. Module-scoped so two orders built in the same tick cannot collide. */
export function nextQueueKey(): string {
  keySeq += 1;
  return 'q' + String(keySeq);
}

/** Test seam — the suite asserts on keys, and a shared counter makes that brittle. */
export function resetQueueKeys(): void {
  keySeq = 0;
}

export function queueOf(orders: readonly OrderDraft[]): readonly QueuedOrder[] {
  return orders.map((order) => ({ key: nextQueueKey(), order }));
}

export function toDrafts(queue: readonly QueuedOrder[]): readonly OrderDraft[] {
  return queue.map((entry) => entry.order);
}

/** Deep equality on the wire shape — the only comparison `matchOf` needs. */
function sameOrder(a: OrderDraft, b: OrderDraft): boolean {
  if (a.asset !== b.asset || a.mission !== b.mission) return false;
  if (JSON.stringify(a.target) !== JSON.stringify(b.target)) return false;
  return JSON.stringify(a.params ?? {}) === JSON.stringify(b.params ?? {});
}

/** How many orders this queue already holds for an asset — the capacity reading. */
export function queuedMissionsByAsset(
  queue: readonly QueuedOrder[],
  exceptKey?: string,
): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const entry of queue) {
    if (entry.key === exceptKey) continue;
    counts[entry.order.asset] = (counts[entry.order.asset] ?? 0) + 1;
  }
  return counts;
}

export function moveOrder(
  queue: readonly QueuedOrder[],
  key: string,
  direction: -1 | 1,
): readonly QueuedOrder[] {
  const from = queue.findIndex((entry) => entry.key === key);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= queue.length) return queue;
  const next = [...queue];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return queue;
  next.splice(to, 0, moved);
  return next;
}

/** Drag-to-reorder: `fromKey` lands where `toKey` currently sits. */
export function reorderQueue(
  queue: readonly QueuedOrder[],
  fromKey: string,
  toKey: string,
): readonly QueuedOrder[] {
  if (fromKey === toKey) return queue;
  const from = queue.findIndex((entry) => entry.key === fromKey);
  const to = queue.findIndex((entry) => entry.key === toKey);
  if (from < 0 || to < 0) return queue;
  const next = [...queue];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return queue;
  next.splice(to, 0, moved);
  return next;
}

/**
 * Match an arriving `CellOrdersState` to the send that produced it, and hang its
 * rejections and warnings on stable keys.
 *
 * Two orders that are element-for-element identical — a duplicate the operator
 * made on purpose — are genuinely indistinguishable inside one send, and the
 * server treats them the same way, so which of the two keys takes the rejection
 * is arbitrary and both would carry the same reason.
 */
export function alignFeedback(
  sends: readonly SentDraft[],
  state: CellOrdersState | null,
): OrderFeedback {
  if (state === null) return NO_FEEDBACK;
  if (state.rejected.length === 0 && state.warnings.length === 0) {
    return { ...NO_FEEDBACK, matchedSeq: matchOf(sends, state)?.seq ?? null };
  }

  const send = matchOf(sends, state);
  if (send === null) {
    return {
      rejections: new Map(),
      warnings: new Map(),
      unplaced: state.rejected,
      matchedSeq: null,
    };
  }

  const rejections = new Map<string, OrderRejection>();
  const unplaced: OrderRejection[] = [];
  for (const rejection of state.rejected) {
    const key = send.keys[rejection.index];
    if (key === undefined) unplaced.push(rejection);
    else rejections.set(key, rejection);
  }

  const warnings = new Map<string, OrderWarningWire[]>();
  for (const warning of state.warnings) {
    const key = send.keys[warning.index];
    if (key === undefined) continue;
    const list = warnings.get(key) ?? [];
    list.push(warning);
    warnings.set(key, list);
  }

  return { rejections, warnings, unplaced, matchedSeq: send.seq };
}

function matchOf(sends: readonly SentDraft[], state: CellOrdersState): SentDraft | null {
  const rejected = new Set(state.rejected.map((rejection) => rejection.index));
  for (let index = sends.length - 1; index >= 0; index -= 1) {
    const send = sends[index];
    if (send === undefined) continue;
    const expected = send.orders.filter((_unused, position) => !rejected.has(position));
    if (expected.length !== state.accepted.length) continue;
    if (expected.every((order, position) => sameOrder(order, state.accepted[position]!))) {
      return send;
    }
  }
  return null;
}
